import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { transformRoute } from "helpers";
import { formatDateParam, transformDateToLocalDateString } from "helpers/common";
import {
  NotificationCalendarDay,
  NotificationCalendarItem,
} from "services/api/notifications/types";
import { getNotificationCalendarAPI } from "services/api/notifications/api";
import { Loader } from "ui/components/Loader";
import { Routes as FleetVehicleRoutes } from "ui/modules/fleet/vehicles/navigation/routes";
import {
  buildMonthWeeks,
  buildWeekDays,
  CalendarViewMode,
  getVisibleRange,
} from "ui/modules/notifications/calendarGrid";
import { CalendarToolbar } from "ui/modules/notifications/pages/components/CalendarToolbar";
import { DEFAULT_VIEW } from "ui/modules/notifications/pages/components/CalendarToolbar/consts";
import DayDetailsModal from "./components/DayDetailsModal/DayDetailsModal";
import MonthGrid from "./components/MonthGrid/MonthGrid";
import WeekGrid from "./components/WeekGrid/WeekGrid";
import { MONTH_NAMES } from "./consts";

const addDays = (date: Date, days: number) => {
  const next = new Date(date);
  next.setDate(next.getDate() + days);

  return next;
};

const CalendarView: React.FC = () => {
  const navigate = useNavigate();

  const [currentDate, setCurrentDate] = useState(new Date());
  const [view, setView] = useState<CalendarViewMode>(DEFAULT_VIEW);
  const [calendarData, setCalendarData] = useState<NotificationCalendarDay[]>([]);
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const weeks = useMemo(() => buildMonthWeeks(currentDate), [currentDate]);
  const weekDays = useMemo(() => buildWeekDays(currentDate), [currentDate]);
  const range = useMemo(() => getVisibleRange(currentDate, view), [currentDate, view]);

  const fetchCalendar = useCallback(async () => {
    setIsLoading(true);
    try {
      const result = await getNotificationCalendarAPI(
        formatDateParam(range.from)!,
        formatDateParam(range.to)!
      );
      setCalendarData(result);
    } catch {
      // greška ide kroz createApiCall
    }
    setIsLoading(false);
  }, [range.from, range.to]);

  useEffect(() => {
    fetchCalendar();
    setSelectedDate(null);
  }, [fetchCalendar]);

  const itemsByDate = useMemo(() => {
    const map = new Map<string, NotificationCalendarItem[]>();
    calendarData.forEach((day) => {
      // Backend vraća datum s vremenskim dijelom, mreža ključeve gradi po danu.
      map.set(day.date.split("T")[0], day.notifications);
    });

    return map;
  }, [calendarData]);

  const handlePrev = useCallback(() => {
    setCurrentDate((current) =>
      view === "week"
        ? addDays(current, -7)
        : new Date(current.getFullYear(), current.getMonth() - 1, 1)
    );
  }, [view]);

  const handleNext = useCallback(() => {
    setCurrentDate((current) =>
      view === "week"
        ? addDays(current, 7)
        : new Date(current.getFullYear(), current.getMonth() + 1, 1)
    );
  }, [view]);

  const handleToday = useCallback(() => {
    setCurrentDate(new Date());
  }, []);

  // Detalji se otvaraju samo za dane koji imaju obavijesti — prazan modal nema smisla.
  const handleDayClick = useCallback(
    (date: Date) => {
      const key = formatDateParam(date)!;
      if (!itemsByDate.get(key)?.length) return;

      setSelectedDate(key);
    },
    [itemsByDate]
  );

  const handleDetailsHide = useCallback(() => {
    setSelectedDate(null);
  }, []);

  const handleVehicleClick = useCallback(
    (vehicleId: string) => {
      navigate(
        transformRoute(FleetVehicleRoutes.FleetVehicleDetailPageRouter, { id: vehicleId })
      );
    },
    [navigate]
  );

  const title = `${MONTH_NAMES[currentDate.getMonth()]} ${currentDate.getFullYear()}`;
  const subtitle = `${transformDateToLocalDateString(range.from)} – ${transformDateToLocalDateString(range.to)}`;

  const selectedItems = selectedDate ? itemsByDate.get(selectedDate) ?? [] : [];

  return (
    <>
      {/* Bez `overflow-hidden` — inače kartica reže otvoreni izbornik prikaza u alatnoj traci. */}
      <div className="bg-white rounded-sm border">
        <CalendarToolbar
          view={view}
          title={title}
          subtitle={subtitle}
          onPrev={handlePrev}
          onNext={handleNext}
          onToday={handleToday}
          onViewChange={setView}
        />

        {isLoading ? (
          <div className="py-16">
            <Loader isLoading />
          </div>
        ) : view === "week" ? (
          <WeekGrid
            days={weekDays}
            itemsByDate={itemsByDate}
            selectedDate={selectedDate}
            onDayClick={handleDayClick}
          />
        ) : (
          <MonthGrid
            weeks={weeks}
            month={currentDate.getMonth()}
            itemsByDate={itemsByDate}
            selectedDate={selectedDate}
            onDayClick={handleDayClick}
          />
        )}
      </div>

      <DayDetailsModal
        show={selectedDate !== null}
        date={selectedDate}
        items={selectedItems}
        onHide={handleDetailsHide}
        onVehicleClick={handleVehicleClick}
      />
    </>
  );
};

export default CalendarView;
